import React, { useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { FcAlarmClock } from 'react-icons/fc';
import { FcCallTransfer } from 'react-icons/fc';
import { BiCoffee } from 'react-icons/bi';
import { FiSave, FiWifi, FiWifiOff } from 'react-icons/fi';
import { FaBluetooth, FaPlay, FaPhoneAlt, FaBackspace, FaPhoneVolume } from "react-icons/fa";
import { FaPause } from "react-icons/fa";
import { FiPhoneCall, FiPhoneIncoming } from "react-icons/fi";
import { MdDialpad } from "react-icons/md";
import { FcConferenceCall } from 'react-icons/fc';
import { BsTelephoneX } from "react-icons/bs";
import { MdOutlinePhoneForwarded } from 'react-icons/md';
import DateTime from 'react-datetime';
import 'react-datetime/css/react-datetime.css';
import Modal from './Modal';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
import Callback from './Callback';
import { CiPause1 } from "react-icons/ci";
import { CiPlay1 } from "react-icons/ci";
import { FcCallback } from "react-icons/fc";

import { FcStart } from "react-icons/fc";
import JsSIP from 'jssip';
import './WebRTCClient.css';
import { getUserInfo } from './Api';


const ANSWER_MODE_KEY = 'webphoneAnswerMode';

// Short display text for the status chip (callStatus itself stays as-is:
// other code compares against these exact strings).
const SHORT_STATUS = {
  'Idle': 'Idle', 'Incoming Call...': 'Ringing', 'In Call': 'Live', 'Dialing...': 'Dialing',
  'Outgoing Call...': 'Dialing', 'Call Ended': 'Ended', 'Call Failed': 'Failed', 'Unregistered': 'No reg',
  'Registration Failed': 'Reg fail', 'Re-registering...': 'Reg…', 'Reconnecting agent...': 'Linking…',
  'Reconnect requested': 'Linked', 'Reconnect failed': 'Link fail',
};
const shortStatus = (s) => SHORT_STATUS[s] || (s && s.length > 10 ? s.slice(0, 9) + '…' : s);

const WebRTCClient = ({ sipUser, sipPass, autoAnswer = true }) => {

  const refclicktocall = useRef(null)
const transferref = useRef(null)
  
  const [registered, setRegistered] = useState(false);
  const [dialNumber, setDialNumber] = useState('');
  const [callStatus, setCallStatus] = useState('Idle');
  // Answer mode, per browser: 'auto' answers incoming calls after 1 s (the
  // default — the nailed-up agent leg and dialer calls just connect); 'popup'
  // shows an Answer / Reject card at the bottom right instead. Kept in a ref
  // as well so switching never re-creates the SIP user agent (that would drop
  // the agent's connected leg).
  const [answerMode, setAnswerMode] = useState(() => {
    try { return localStorage.getItem(ANSWER_MODE_KEY) === 'popup' ? 'popup' : (autoAnswer ? 'auto' : 'popup') }
    catch { return autoAnswer ? 'auto' : 'popup' }
  });
  const answerModeRef = useRef(answerMode);
  const [incoming, setIncoming] = useState(null); // { name, number } while a popup call rings
  const [dialpadOpen, setDialpadOpen] = useState(false);

  const handleDialpadKey = (key) => setDialNumber(d => d + key);
  const handleDialpadBackspace = () => setDialNumber(d => d.slice(0, -1));
  const handleDialpadCall = () => {
    if (!dialNumber) return;
    startCall();
    setDialpadOpen(false);
  };

  const remoteAudio = useRef(null);
  const uaRef = useRef(null);
  const sessionRef = useRef(null);

  const SIP_DOMAIN = process.env.REACT_APP_SIP_DOMAIN || 'webrtc.nsssoft.in'
  const SIP_URI = `sip:${sipUser}@${SIP_DOMAIN}`;
  const WS_URI = process.env.REACT_APP_SIP_WS_URI || `wss://${SIP_DOMAIN}:8089/ws`;
  const SIP_PASS = `${sipPass}`;

  useEffect(() => {
    const socket = new JsSIP.WebSocketInterface(WS_URI);

    const config = {
      sockets: [socket],
      uri: SIP_URI,
      password: SIP_PASS,
      session_timers: false,
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
    };

    const ua = new JsSIP.UA(config);
    uaRef.current = ua;

    ua.on('registered', () => {
      setRegistered(true);
      setCallStatus('Idle');
    });

    ua.on('unregistered', () => {
      setRegistered(false);
      setCallStatus('Unregistered');
    });

    ua.on('registrationFailed', () => {
      setRegistered(false);
      setCallStatus('Registration Failed');
    });

    // Plays the remote party's audio. The agent's nailed-up session is
    // auto-answered with no user gesture, so the browser's autoplay policy
    // can reject play() — previously that was only logged, leaving the agent
    // hearing nothing for the whole session (one-way audio). Retry on the
    // next click/keypress when that happens.
    const playRemote = () => {
      const el = remoteAudio.current;
      if (!el || !el.srcObject) return;
      el.play().catch(err => {
        console.warn('Remote audio play blocked, retrying on next user interaction:', err);
        const retry = () => {
          document.removeEventListener('click', retry, true);
          document.removeEventListener('keydown', retry, true);
          if (remoteAudio.current && remoteAudio.current.srcObject) {
            remoteAudio.current.play().catch(e => console.warn('Remote audio play failed:', e));
          }
        };
        document.addEventListener('click', retry, true);
        document.addEventListener('keydown', retry, true);
      });
    };

    const attachRemoteAudio = (pc) => {
      if (!pc || !remoteAudio.current) return;
      const tracks = pc.getReceivers()
        .map(r => r.track)
        .filter(t => t && t.kind === 'audio');
      if (tracks.length === 0) return;
      remoteAudio.current.srcObject = new MediaStream(tracks);
      playRemote();
    };

    ua.on('newRTCSession', ({ session, originator }) => {
      const previous = sessionRef.current;
      sessionRef.current = session;

      // Attach as soon as the remote track exists, not only on 'accepted'.
      const hookPc = (pc) => {
        if (!pc) return;
        pc.addEventListener('track', (e) => {
          if (e.track.kind !== 'audio' || !remoteAudio.current) return;
          remoteAudio.current.srcObject = (e.streams && e.streams[0]) || new MediaStream([e.track]);
          playRemote();
        });
      };
      if (session.connection) hookPc(session.connection);
      else session.on('peerconnection', ({ peerconnection }) => hookPc(peerconnection));
      session.on('confirmed', () => {
        attachRemoteAudio(session.connection);
        // Only one call may own the microphone. A leftover call (an old login
        // leg the server never hung up) keeps competing for it — on mobile
        // browsers the new call can end up sending no audio at all.
        if (previous && previous !== session && !previous.isEnded()) {
          try { previous.terminate(); } catch (e) { console.warn('Old call terminate failed:', e); }
        }
      });

      if (originator === 'remote') {
        setCallStatus('Incoming Call...');

        if (answerModeRef.current === 'auto') {
          setTimeout(() => {
            if (sessionRef.current && sessionRef.current.isInProgress()) {
              answerCall();
            }
          }, 1000); // Wait 1 second before auto-answering
        } else {
          const ri = session.remote_identity || {};
          setIncoming({ name: ri.display_name || '', number: (ri.uri && ri.uri.user) || '' });
          // The popup goes away however the ringing ends.
          const clear = () => setIncoming(null);
          session.on('accepted', clear);
          session.on('ended', clear);
          session.on('failed', clear);
        }
      } else {
        setCallStatus('Outgoing Call...');
      }

      session.on('accepted', () => {
        setCallStatus('In Call');

        attachRemoteAudio(session.connection);
      });

      // A replaced (older) call ending must not tear down the current one's audio.
      session.on('ended', () => {
        if (sessionRef.current !== session) return;
        setCallStatus('Call Ended');
        sessionRef.current = null;
        if (remoteAudio.current) remoteAudio.current.srcObject = null;
      });

      session.on('failed', () => {
        if (sessionRef.current !== session) return;
        setCallStatus('Call Failed');
        sessionRef.current = null;
        if (remoteAudio.current) remoteAudio.current.srcObject = null;
      });
    });

   // ua.start();

    // const handleKeyDown = (e) => {
    //   const validKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#'];
    //   if (validKeys.includes(e.key)) {
    //     setDialNumber(d => d + e.key);
    //   } else if (e.key === 'Backspace') {
    //     setDialNumber(d => d.slice(0, -1));
    //   } else if (e.key === 'Escape') {
    //     setDialNumber('');
    //   }
    // };

    //window.addEventListener('keydown', handleKeyDown);
    return () => {
      // Stop the JsSIP User-Agent so its WebSocket, timers, and any
      // ongoing session are torn down when the component unmounts (or
      // on logout / reload).
      ua.stop();

      // Release any remote audio stream that may still be attached.
      if (remoteAudio.current) {
        const stream = remoteAudio.current.srcObject;
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }
        remoteAudio.current.srcObject = null;
      }

      // Clear the session reference so stale callbacks don't fire.
      sessionRef.current = null;
    };
    // Created once per mount: changing the answer mode must not restart the
    // user agent (see answerModeRef).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mobile browsers stop the microphone while the screen is locked or the app
  // is in the background but keep playing the far end — the customer then
  // hears nothing. Keep the screen awake while this page is open, and when it
  // is in the foreground, swap a fresh mic track into the live call if the old
  // one was killed.
  useEffect(() => {
    let wakeLock = null;
    const requestWakeLock = async () => {
      if (!('wakeLock' in navigator) || document.visibilityState !== 'visible') return;
      try { wakeLock = await navigator.wakeLock.request('screen'); }
      catch (e) { console.warn('Screen wake lock unavailable:', e); }
    };
    const ensureMic = async () => {
      const pc = sessionRef.current && sessionRef.current.connection;
      if (!pc || !navigator.mediaDevices) return;
      const sender = pc.getSenders().find(s => !s.track || s.track.kind === 'audio');
      if (!sender) return;
      const old = sender.track;
      if (old && old.readyState === 'live' && !old.muted) return;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        const track = stream.getAudioTracks()[0];
        if (old) track.enabled = old.enabled; // keep the agent's own mute state
        await sender.replaceTrack(track);
        if (old) old.stop();
        console.info('Microphone re-acquired for the live call');
      } catch (e) {
        console.warn('Microphone re-acquire failed:', e);
      }
    };
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      requestWakeLock();
      ensureMic();
    };
    requestWakeLock();
    document.addEventListener('visibilitychange', onVisibility);
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') ensureMic();
    }, 5000);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(timer);
      if (wakeLock) wakeLock.release().catch(() => {});
    };
  }, []);

  const startCall = () => {
    if (!dialNumber) return alert('Enter number');
    if (sessionRef.current) return alert('Already in a call');

    uaRef.current.call(`sip:${dialNumber}@${SIP_DOMAIN}`, {
      eventHandlers: {
        progress: () => setCallStatus('Dialing...'),
        confirmed: () => setCallStatus('In Call'),
        ended: () => {
          setCallStatus('Call Ended');
          sessionRef.current = null;
          if (remoteAudio.current) remoteAudio.current.srcObject = null;
        },
        failed: () => {
          setCallStatus('Call Failed');
          sessionRef.current = null;
          if (remoteAudio.current) remoteAudio.current.srcObject = null;
        }
      },
      mediaConstraints: { audio: true, video: false }
    });
  };

  const answerCall = () => {
    if (sessionRef.current && sessionRef.current.isInProgress()) {
      sessionRef.current.answer({ mediaConstraints: { audio: true, video: false } });
      setCallStatus('In Call');
    }
  };

  const hangUp = () => {
    if (sessionRef.current) {
      sessionRef.current.terminate();
      setCallStatus('Call Ended');
      sessionRef.current = null;
      if (remoteAudio.current) remoteAudio.current.srcObject = null;
    }
  };

  const reRegister = () => {
    if (uaRef.current) {
      setCallStatus('Re-registering...');
      uaRef.current.stop();
      setTimeout(() => uaRef.current.start(), 500);
    }
  };

  // Manual fallback when the post-login ARI bridge call didn't arrive
  // (e.g. JsSIP registered after the originate timed out). Re-runs the
  // server-side agentSessionManager.login() so Asterisk dials the agent again.
  const reconnectAgent = async () => {
    try {
      setCallStatus('Reconnecting agent...');
      const res = await fetch(process.env.REACT_APP_IP + '/User/AgentReconnect', {
        method: 'POST',
        credentials: 'include',
      });
      const json = await res.json();
      setCallStatus(json.type === 'OK' ? 'Reconnect requested' : (json.msg || 'Reconnect failed'));
    } catch (e) {
      setCallStatus('Reconnect failed');
    }
  };

  const setMode = (mode) => {
    answerModeRef.current = mode;
    setAnswerMode(mode);
    try { localStorage.setItem(ANSWER_MODE_KEY, mode) } catch { /* private mode */ }
  };

  const rejectCall = () => {
    const s = sessionRef.current;
    if (s && s.isInProgress()) {
      try { s.terminate({ status_code: 486, reason_phrase: 'Busy Here' }); } catch (e) { console.warn('reject failed:', e); }
    }
    setIncoming(null);
  };

  const transferAgent = async () => {

    transferref.current.click();

    //alert("clicked>>>>>>>>>");

  }

  const callActive = ['Dialing...', 'In Call', 'Incoming Call...'].includes(callStatus);



 const [isOpen, setIsOpen] = useState(false);
  const [hidecustmobile, setHidecustmobile] = useState();
  const [agentname, setAgentname] = useState();
  const [number, setNumber] = useState("NA");
   const [transnumber, setTransnumber] = useState("NA");
  const [transfernumfixed, setTransfernumfixed] = useState("NA");
  const [remark, setRemarks] = useState();
  const [busyflagnew, setBusyflagnew] = useState();
  const [elements, setElements] = useState(null);
  const [userstatus, setuserstatus] = useState(null);
  const [camp, setcamp] = useState();
  const [custmobile, setcustmobile] = useState();
  const [type, setType] = useState();
  const [loginstatus, setloginstatus] = useState("NA");


  const [callanstime, setCallanstime] = useState();

  const [useridcrm, setUseridcrm] = useState();

  const [sip, setsip] = useState('');
   const [testcb, setTestcb] = useState("NA");

  const [cb, setCb] = useState("NA");

  const [ptp, setPtp] = useState("NA");

  const [vdate, setVdate] = useState("NA");
  const [llead, setLlead] = useState("NA");
  const [disp, setDisp] = useState("NA");
 





 const setCallback = (e) => {
     if (e === "CALLBACK") {
      setCb("CALLBACK")

    }else if (e === "PTP") {
      setPtp("PTP")

    } else {

      setCb("NA")
    }
    // alert(e)

    //var abc=e;
    //setSelectedDate('')
    const separatedData = e.split("|");
    //alert(separatedData[0])
    //alert(separatedData[1])
    //alert(separatedData[2])

    //setDisp(e);
    setDisp(separatedData[0])
   
    setTestcb(separatedData[0])

    // alert(separatedData[1])
    if (separatedData.length == 3) {
     
      if (separatedData[2] === "Yes") {
        // alert("setting")
        setVdate("Yes")
      } else {
        setVdate("No")
      }
      if (separatedData[1] === "Yes") {
        // alert("setting")
        setLlead("Yes")
      } else {
        setLlead("No")
      }
    } else {
      setVdate("No")
      setLlead("No")
    }




    // if (e === "CALLBACK") {
    //   setCb("CALLBACK")

    // }else if (e === "PTP") {
    //   setPtp("PTP")

    // } else {

    //   setCb("NA")
    // }


  }
 const [selectedDate, setSelectedDate] = useState('');

  const handleDateChange = (date) => {
    //alert(date)
    setSelectedDate(date);
    // alert(dayjs(selectedDate).format('YYYY-MM-DD HH:mm:ss'))
  };
  const [mmargin, setMmargin] = useState(true);


  const clicktocall = async (e) => {

    refclicktocall.current.click();
    // console.log(">>>>>clicktocall>>>>>"+e)
    //alert("clicked>>>>>>>>>");

  }


    // Transfer number options are managed by Crm.js via React state (transferNumbers).
    // The old innerHTML-based optionforfixtransfer has been removed to fix XSS vulnerability.

    const clicktocallsubmit = async (e) => {
      e.preventDefault();
      refclicktocall.current.click();
      //console.log(">>>>number>>>>"+number)
      var formdata = new FormData()
      formdata.append('number', number)
      const response = await fetch(process.env.REACT_APP_IP + "/User/Clicktocallnp", {
        method: 'POST',
        // headers: {
        //     'Content-Type': 'application/json'
        // },
  
        credentials: 'include',
        body: formdata
        // body: JSON.stringify({ campname: campname,campaign_mode: campaign_mode })
      });
      const json = await response.json()
      // console.log(json);
      if (json.type === "OK") {
        // props.showAlert(json.msg, "success");
        Swal.fire({
          position: 'top-end',
          icon: 'success',
          title: json.msg,
          background: '#e9ecef',
          // title: 'Done Successfully ',
          //background: primary,
          showConfirmButton: false,
          timer: 2500
        })
      } else {
        //props.showAlert(json.msg, "danger");
        Swal.fire({
          position: 'top-end',
          icon: 'error',
          title: json.msg,
          background: '#e9ecef',
          // title: 'Done Successfully ',
          //background: primary,
          showConfirmButton: false,
          timer: 2500
        })
      }
  
      setNumber('')
  
  
    }



    const hangupAgent = async () => {
        // e.preventDefault();
        // var formdata = new FormData()
        // formdata.append('id', e)
        const response = await fetch(process.env.REACT_APP_IP + "/User/HangupAgentApi", {
          method: 'POST',
          // headers: {
          //     'Content-Type': 'application/json'
          // },
    
          credentials: 'include'
          // body: JSON.stringify({ campname: campname,campaign_mode: campaign_mode })
        });
        const json = await response.json()
        // console.log(json);
        if (json.type === "OK") {
          // props.showAlert(json.msg, "success");
          Swal.fire({
            position: 'top-end',
            icon: 'success',
            title: json.msg,
            background: '#e9ecef',
            // title: 'Done Successfully ',
            //background: primary,
            showConfirmButton: false,
            timer: 2500
          })
        } else {
          //props.showAlert(json.msg, "danger");
          Swal.fire({
            position: 'top-end',
            icon: 'error',
            title: json.msg,
            background: '#e9ecef',
            // title: 'Done Successfully ',
            //background: primary,
            showConfirmButton: false,
            timer: 2500
          })
        }
    
    
    
      }


    const onPause = async (e) => {
    
        const response = await fetch(process.env.REACT_APP_IP + "/User/onPause", {
          method: 'POST',
          // headers: {
          //     'Content-Type': 'application/json'
          // },
    
          credentials: 'include',
          // body: formdata
          // body: JSON.stringify({ campname: campname,campaign_mode: campaign_mode })
        });
        const json = await response.json()
        // console.log(json);
        if (json.type === "OK") {
          // props.showAlert(json.msg, "success");
          Swal.fire({
            position: 'top-end',
            icon: 'success',
            title: json.msg,
            background: '#e9ecef',
            // title: 'Done Successfully ',
            //background: primary,
            showConfirmButton: false,
            timer: 2500
          })
        } else {
          //props.showAlert(json.msg, "danger");
          Swal.fire({
            position: 'top-end',
            icon: 'error',
            title: json.msg,
            background: '#e9ecef',
            // title: 'Done Successfully ',
            //background: primary,
            showConfirmButton: false,
            timer: 2500
          })
        }
    
    
    
      }

       const redialsubmit = async () => {
          // e.preventDefault();
          // refclicktocall.current.click();
          //console.log(">>>>number>>>>"+number)
          // var formdata = new FormData()
          //  formdata.append('number', number)
          const response = await fetch(process.env.REACT_APP_IP + "/User/Redial", {
            method: 'POST',
            // headers: {
            //     'Content-Type': 'application/json'
            // },
      
            credentials: 'include'
            // body: formdata
            // body: JSON.stringify({ campname: campname,campaign_mode: campaign_mode })
          });
          const json = await response.json()
          // console.log(json);
          if (json.type === "OK") {
            // props.showAlert(json.msg, "success");
            Swal.fire({
              position: 'top-end',
              icon: 'success',
              title: json.msg,
              background: '#e9ecef',
              // title: 'Done Successfully ',
              //background: primary,
              showConfirmButton: false,
              timer: 2500
            })
          } else {
            //props.showAlert(json.msg, "danger");
            Swal.fire({
              position: 'top-end',
              icon: 'error',
              title: json.msg,
              background: '#e9ecef',
              // title: 'Done Successfully ',
              //background: primary,
              showConfirmButton: false,
              timer: 2500
            })
          }
      
          //setNumber('')
      
      
        }

// NOTE: previous version polled /User/GetUserinfo every 4s here and
// set ~10 pieces of state that the minimal WebRTCClient UI never reads.
// It also had a broken cleanup (clearInterval returned from inside the
// tick callback, so the interval leaked on unmount and triggered long
// message-handler violations). The shared userInfoStore already polls
// this endpoint once for the whole app, so the local poller is gone.


          const transferAgentfun = async (e) => {
              e.preventDefault();
              transferref.current.click();
              //console.log(">>>>number>>>>"+number)
             // console.log(">>>>transfernumfixed>>>>"+transfernumfixed)
             // console.log(">>>>number>>>>"+(number === "NA"))
             // console.log(">>>>number>>>>"+(number == "NA"))
             // console.log(">>>>number>>>>"+(number == 'NA'))
              //console.log(">>>>number>>>>"+number == NA)
              //if(number === "NA")
              //{
                //number=transfernumfixed;
              //  console.log("############"+transfernumfixed)
               // setAnumber(transfernumfixed);
                //console.log("############"+anumber)
              //}
              //console.log(">>>>number>>>>"+number)
              var formdata = new FormData()
              formdata.append('number', transnumber)
              formdata.append('transfernumfixed', transfernumfixed)
              //formdata.append('agent', agent)
              const response = await fetch(process.env.REACT_APP_IP + "/User/Transfer", {
                method: 'POST',
                // headers: {
                //     'Content-Type': 'application/json'
                // },
          
                credentials: 'include',
                body: formdata
                // body: JSON.stringify({ campname: campname,campaign_mode: campaign_mode })
              });
              const json = await response.json()
              // console.log(json);
              if (json.type === "OK") {
                // props.showAlert(json.msg, "success");
                Swal.fire({
                  position: 'top-end',
                  icon: 'success',
                  title: json.msg,
                  background: '#e9ecef',
                  // title: 'Done Successfully ',
                  //background: primary,
                  showConfirmButton: false,
                  timer: 2500
                })
              } else {
                //props.showAlert(json.msg, "danger");
                Swal.fire({
                  position: 'top-end',
                  icon: 'error',
                  title: json.msg,
                  background: '#e9ecef',
                  // title: 'Done Successfully ',
                  //background: primary,
                  showConfirmButton: false,
                  timer: 2500
                })
              }
          
              setNumber('')
          
          
            }

             useEffect(() => {

              reRegister()
   
                    }, []);


  const ringing = callStatus === 'Incoming Call...';

  // A slim, muted strip: the web-phone mostly runs itself (auto-answer +
  // nailed-up leg); these controls are for the odd case it needs a nudge.
  return (
    <>
      <span className='wp-bar-label'>Phone</span>
      <span className={`wp-bar-state wp-state-${callStatus === 'In Call' ? 'active' : ringing ? 'incoming' : 'idle'}`}
        title={`Web-phone status: ${callStatus}`}>
        {callStatus === 'In Call' ? <FaPhoneVolume size={12} /> : ringing ? <FiPhoneIncoming size={12} strokeWidth={3} /> : <FiPhoneCall size={12} strokeWidth={3} />}
        {shortStatus(callStatus)}
      </span>
      <button className={`wp-bar-btn wp-ic-reg ${registered ? 'is-ok' : 'is-bad'}`} onClick={reRegister}
        title={registered ? 'SIP registered — click to re-register' : 'Not registered — click to re-register'}>
        {registered ? <FiWifi size={14} strokeWidth={3} /> : <FiWifiOff size={14} strokeWidth={3} />}<span>Reg</span>
      </button>
      <button className='wp-bar-btn wp-ic-link' onClick={reconnectAgent}
        title="Connect agent: re-run the server-side agent login if no call arrived after login">
        <FiPhoneCall size={14} strokeWidth={3} /><span>Link</span>
      </button>
      <button className={`wp-bar-btn wp-ic-ans ${ringing ? 'is-ring' : ''}`} onClick={answerCall}
        disabled={!registered || !ringing} title="Answer the incoming web-phone call">
        <FaPlay size={11} /><span>Ans</span>
      </button>
      <button className='wp-bar-btn wp-ic-end' onClick={hangUp} disabled={!callActive} title="Hang up the current web-phone call">
        <BsTelephoneX size={13} /><span>End</span>
      </button>
      <button className='wp-bar-btn wp-ic-dial' onClick={() => setDialpadOpen(true)} title="Open the dialpad to make a call">
        <MdDialpad size={15} /><span>Dial</span>
      </button>
      <span className='wp-mode' role='group' aria-label='Incoming calls'
        title="Incoming calls: answer automatically, or show a popup to answer / reject">
        <span className='wp-mode-label'>In</span>
        <button type='button' className={answerMode === 'auto' ? 'is-on' : ''} aria-pressed={answerMode === 'auto'}
          onClick={() => setMode('auto')}>Auto</button>
        <button type='button' className={answerMode === 'popup' ? 'is-on' : ''} aria-pressed={answerMode === 'popup'}
          onClick={() => setMode('popup')}>Popup</button>
      </span>

      {incoming && (
        <div className='wp-popup' role='alertdialog' aria-live='assertive' aria-label='Incoming call'>
          <div className='wp-popup-ring'><FiPhoneIncoming size={18} /></div>
          <div className='wp-popup-info'>
            <span className='wp-popup-title'>Incoming call</span>
            <strong>{incoming.name || incoming.number || 'Unknown caller'}</strong>
            {incoming.name && incoming.number && incoming.name !== incoming.number && <small>{incoming.number}</small>}
          </div>
          <div className='wp-popup-actions'>
            <button type='button' className='wp-popup-answer' onClick={answerCall} autoFocus>
              <FaPhoneAlt size={13} /> Answer
            </button>
            <button type='button' className='wp-popup-reject' onClick={rejectCall}>
              <BsTelephoneX size={13} /> Reject
            </button>
          </div>
        </div>
      )}
      <audio ref={remoteAudio} autoPlay className="d-none" />

      {dialpadOpen && (
        <div className="wp-dialpad-overlay" onClick={() => setDialpadOpen(false)}>
          <div className="wp-dialpad-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wp-dialpad-header">
              <div className="wp-dialpad-icon-ring">
                <FaPhoneAlt size={18} />
              </div>
              <h5>Web-Phone Dialpad</h5>
              <p>Dial a number using your SIP extension</p>
              <button type="button" className="wp-dialpad-close" onClick={() => setDialpadOpen(false)} aria-label="Close">&times;</button>
            </div>
            <div className="wp-dialpad-body">
              <div className="wp-dialpad-display">
                <input
                  type="text"
                  className="wp-dialpad-input"
                  placeholder="Enter number"
                  value={dialNumber}
                  onChange={(e) => setDialNumber(e.target.value.replace(/[^0-9*#+]/g, ''))}
                  autoFocus
                />
                {dialNumber && (
                  <button type="button" className="wp-dialpad-clear" onClick={handleDialpadBackspace} aria-label="Backspace">
                    <FaBackspace size={16} />
                  </button>
                )}
              </div>
              <div className="wp-dialpad-grid">
                {['1','2','3','4','5','6','7','8','9','*','0','#'].map((key) => (
                  <button type="button" key={key} className="wp-dialpad-key" onClick={() => handleDialpadKey(key)}>
                    {key}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className={`wp-dialpad-call ${!registered || !dialNumber || callActive ? 'is-disabled' : ''}`}
                onClick={handleDialpadCall}
                disabled={!registered || !dialNumber || callActive}
              >
                <FaPhoneAlt size={14} /> Call
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WebRTCClient;